/**
 * NCC: <ncc-carousel-pagination> - page indicator bars (and optional previous/next arrows) for a
 * Horizon slideshow-based carousel. Rendered by snippets/ncc-carousel-pagination.liquid right
 * after the carousel it controls. One bar per scroll stop and the arrows move ONE CARD at a time
 * (2026-09-12: the way Swiper paginates on the Stack/Lager demo - Miles found page-sized jumps
 * unfamiliar), so stops = items - visible columns + 1, the visible columns measured from the slide
 * geometry so it follows the section's desktop/mobile column settings; hidden when everything fits.
 * `step="page"` on the element restores page-sized bars and arrow steps.
 *
 * The active page is read from the scroller's own scroll position, not from the slideshow's
 * `current` slide: Horizon's carousels scroll full-bleed with the page gutters as padding, and
 * its IntersectionObserver counts a slide as current once 70% of it shows anywhere in that
 * full-width scroller, so on a wide monitor the card sitting in the left gutter wins and the
 * bars would point at the page before the one on screen (arrows then re-target the same page
 * forever).
 *
 * Clicking a bar or an arrow animates the scroller itself (Stack/Lager easing, 600ms) instead of
 * relying on the browser's native smooth scrolling, which some browsers and OS settings turn into
 * an instant jump. Scroll snapping is paused for the ride and the slideshow is marked `actioned`
 * so Horizon renders every card (it otherwise leaves far-away cards blank until first touch).
 *
 * Trackpad swipes (horizontal wheel events) move exactly one card per gesture in the swipe
 * direction, the way the Stack/Lager demo does. Left to the browser, a swipe shorter than half a
 * card snaps back to where it started, which reads as the carousel fighting the user.
 */
import { SlideshowSelectEvent } from '@theme/events';

const DURATION_MS = 600;
const WHEEL_GESTURE_GAP_MS = 220; // wheel events further apart than this start a new swipe

/** cubic-bezier(0.72, 0.14, 0.07, 0.96) - the easing the Lager demo uses for its carousels */
const ease = cubicBezier(0.72, 0.14, 0.07, 0.96);

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class NccCarouselPagination extends HTMLElement {
  /** @type {HTMLElement | null} */
  #slideshow = null;
  /** @type {ResizeObserver | null} */
  #observer = null;
  #page = 0;
  #animation = 0;
  #scrollFrame = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #wheelTimer;
  #wheelActive = false;

  connectedCallback() {
    this.#slideshow = this.#findSlideshow();
    if (!this.#slideshow) return;

    this.#render();
    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    this.#observer = new ResizeObserver(() => this.#render());
    this.#observer.observe(this.#slideshow);
    this.querySelector('[data-arrow="previous"]')?.addEventListener('click', () => this.#go(-1));
    this.querySelector('[data-arrow="next"]')?.addEventListener('click', () => this.#go(1));
    this.#scroller?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#scroller?.addEventListener('wheel', this.#onWheel, { passive: false });
    // a finger or mouse on the carousel cancels an animation in flight instead of fighting it
    this.#scroller?.addEventListener('pointerdown', this.#cancelAnimation, { passive: true });
    this.#scroller?.addEventListener('touchstart', this.#cancelAnimation, { passive: true });
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#scroller?.removeEventListener('wheel', this.#onWheel);
    this.#scroller?.removeEventListener('pointerdown', this.#cancelAnimation);
    this.#scroller?.removeEventListener('touchstart', this.#cancelAnimation);
    this.#observer?.disconnect();
    this.#cancelAnimation();
    cancelAnimationFrame(this.#scrollFrame);
  }

  #findSlideshow() {
    let node = this.previousElementSibling;
    while (node) {
      const found = node.matches('slideshow-component') ? node : node.querySelector('slideshow-component');
      if (found instanceof HTMLElement) return found;
      node = node.previousElementSibling;
    }
    return null;
  }

  get #bars() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-bars]') ?? this);
  }

  get #scroller() {
    return /** @type {HTMLElement | null} */ (this.#slideshow?.querySelector('slideshow-slides') ?? null);
  }

  get #slides() {
    const slides = /** @type {any} */ (this.#slideshow)?.slides;
    return Array.isArray(slides) ? slides : [...(this.#slideshow?.querySelectorAll('slideshow-slide') ?? [])];
  }

  /** Distance from one slide's left edge to the next (slide width + column gap); 0 before layout. */
  get #stride() {
    const slides = this.#slides;
    const scroller = this.#scroller;
    if (!scroller || !slides.length) return 0;
    const slideWidth = slides[0].getBoundingClientRect().width;
    if (!slideWidth) return 0;
    return slideWidth + (parseFloat(getComputedStyle(scroller).columnGap) || 0);
  }

  get #perPage() {
    const scroller = this.#scroller;
    const stride = this.#stride;
    if (!scroller || !stride) return 1;
    const style = getComputedStyle(scroller);
    const gap = parseFloat(style.columnGap) || 0;
    // clientWidth includes the page gutters Horizon applies as padding on the scroller
    const inner = scroller.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    return Math.max(1, Math.round((inner + gap) / stride));
  }

  get #pageMode() {
    return this.getAttribute('step') === 'page';
  }

  /** Number of bars: scroll stops in card mode, pages in page mode. */
  get #pages() {
    const per = this.#perPage;
    if (this.#pageMode) return Math.ceil(this.#slides.length / per);
    return Math.max(1, this.#slides.length - per + 1);
  }

  /** First slide index of bar `n`. */
  #slideFor(n) {
    return this.#pageMode ? n * this.#perPage : n;
  }

  /** Index of the slide sitting on the scroll padding edge, read from the scroll position. */
  get #index() {
    const scroller = this.#scroller;
    const stride = this.#stride;
    if (!scroller || !stride) return 0;
    const last = Math.max(0, this.#slides.length - 1);
    return Math.min(Math.max(Math.round(Math.abs(scroller.scrollLeft) / stride), 0), last);
  }

  /** Page the scroller is on; the last page as soon as it cannot scroll any further. */
  get #currentPage() {
    const scroller = this.#scroller;
    const last = Math.max(0, this.#pages - 1);
    if (!scroller) return 0;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll > 0 && Math.abs(scroller.scrollLeft) >= maxScroll - 1) return last;
    const index = this.#pageMode ? Math.floor(this.#index / this.#perPage) : this.#index;
    return Math.min(index, last);
  }

  #render() {
    const pages = this.#pages;
    this.hidden = pages <= 1;
    const bars = this.#bars;
    bars.replaceChildren();
    for (let page = 0; page < pages; page++) {
      const bar = document.createElement('button');
      bar.type = 'button';
      bar.className = 'ncc-pagination__bar';
      bar.setAttribute('aria-label', `${page + 1} / ${pages}`);
      bar.addEventListener('click', () => this.#scrollToSlide(this.#slideFor(page)));
      bars.append(bar);
    }
    this.#update();
  }

  /** @param {number} delta - bars to move (one card in card mode, one page in page mode) */
  #go(delta) {
    const page = Math.min(Math.max(this.#currentPage + delta, 0), this.#pages - 1);
    this.#scrollToSlide(this.#slideFor(page));
  }

  /** @param {number} delta - slides to move */
  #step(delta) {
    const last = Math.max(0, this.#slides.length - this.#perPage);
    this.#scrollToSlide(Math.min(Math.max(this.#index + delta, 0), last));
  }

  #cancelAnimation = () => {
    if (!this.#animation) return;
    cancelAnimationFrame(this.#animation);
    this.#animation = 0;
    const scroller = this.#scroller;
    scroller?.style.removeProperty('scroll-snap-type');
    scroller?.style.removeProperty('scroll-behavior');
  };

  #onWheel = (/** @type {WheelEvent} */ event) => {
    // only sideways gestures; vertical wheel keeps scrolling the page
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || event.deltaX === 0) return;
    event.preventDefault();
    clearTimeout(this.#wheelTimer);
    this.#wheelTimer = setTimeout(() => (this.#wheelActive = false), WHEEL_GESTURE_GAP_MS);
    if (this.#wheelActive) return; // momentum events of the same swipe
    this.#wheelActive = true;
    this.#step(event.deltaX > 0 ? 1 : -1);
  };

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#update();
    });
  };

  /**
   * Animate the scroller so the slide at `index` lands on the scroll padding edge.
   * @param {number} index
   */
  #scrollToSlide(index) {
    const scroller = this.#scroller;
    const slide = this.#slides[index];
    if (!scroller || !(slide instanceof HTMLElement)) return;

    this.#slideshow?.setAttribute('actioned', '');
    const padding = parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const from = scroller.scrollLeft;
    const target = Math.min(Math.max(slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left + from - padding, 0), maxScroll);
    if (Math.abs(target - from) < 1) return;

    this.#cancelAnimation();
    if (prefersReducedMotion()) {
      scroller.scrollLeft = target;
      this.#update();
      return;
    }

    scroller.style.scrollSnapType = 'none';
    scroller.style.scrollBehavior = 'auto';
    const start = performance.now();
    const step = (/** @type {number} */ now) => {
      const progress = Math.min((now - start) / DURATION_MS, 1);
      scroller.scrollLeft = from + (target - from) * ease(progress);
      if (progress < 1) {
        this.#animation = requestAnimationFrame(step);
      } else {
        scroller.scrollLeft = target;
        this.#animation = 0;
        scroller.style.removeProperty('scroll-snap-type');
        scroller.style.removeProperty('scroll-behavior');
        this.#update();
      }
    };
    this.#animation = requestAnimationFrame(step);
  }

  /** Point the bars and arrows at the page the scroller is actually on. */
  #update() {
    const last = Math.max(0, this.#bars.children.length - 1);
    this.#page = Math.min(this.#currentPage, last);
    [...this.#bars.children].forEach((bar, i) => bar.setAttribute('aria-current', i === this.#page ? 'true' : 'false'));
    const previous = this.querySelector('[data-arrow="previous"]');
    const next = this.querySelector('[data-arrow="next"]');
    if (previous instanceof HTMLButtonElement) previous.disabled = this.#page <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#page >= last;
  }

  // Horizon's own selection events (keyboard, autoplay) still trigger a re-read of the position
  #onSelect = () => this.#update();
}

/**
 * Returns an easing function for a CSS cubic-bezier curve.
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 */
function cubicBezier(x1, y1, x2, y2) {
  const ax = 3 * x1 - 3 * x2 + 1;
  const bx = 3 * x2 - 6 * x1;
  const cx = 3 * x1;
  const ay = 3 * y1 - 3 * y2 + 1;
  const by = 3 * y2 - 6 * y1;
  const cy = 3 * y1;
  const sampleX = (/** @type {number} */ t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (/** @type {number} */ t) => ((ay * t + by) * t + cy) * t;
  const slopeX = (/** @type {number} */ t) => (3 * ax * t + 2 * bx) * t + cx;
  return (/** @type {number} */ x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const dx = sampleX(t) - x;
      if (Math.abs(dx) < 1e-5) break;
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= dx / slope;
    }
    return sampleY(Math.min(Math.max(t, 0), 1));
  };
}

if (!customElements.get('ncc-carousel-pagination')) {
  customElements.define('ncc-carousel-pagination', NccCarouselPagination);
}
